/* eslint-disable @nx/enforce-module-boundaries*/
import { isDevUrl } from '@ps20-frontend/utils';
import { PS20Logger } from '@ps20-frontend/logging';
import stickybits from 'stickybits';
import {
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  InitializeShopPayload
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { Dynatrace } from '@ps20-frontend/dynatrace';
import { localStore as toolbarStore } from '@ps20-frontend/apps/toolbar-store';
import { localStore as dashboardStore } from '@ps20-frontend/apps/dashboard-store';
import { localStore as checkoutStore } from '@ps20-frontend/apps/checkout-store';
import { localStore as phStore } from '@ps20-frontend/apps/ph-store';
import { localStore as jhpStore } from '@ps20-frontend/apps/jhp-store';
import { localStore as rsStore } from '@ps20-frontend/apps/rs-store';
import { localStore as rsVermietungStore } from '@ps20-frontend/apps/rs-vermietung-store';
import { localStore as thhHundStore } from '@ps20-frontend/apps/thh-hund-store';
import { localStore as thhPferdStore } from '@ps20-frontend/apps/thh-pferd-store';
import { localStore as tgvStore } from '@ps20-frontend/apps/tgv-store';
import { Unsubscribe } from 'redux';
import { Role } from '@ps20/bff-models';
import { appIsInitilized } from './store/local.actions';
import { LocationChangedHandler } from './locationChangedHandler';
import { UrlParameterLoader } from './urlParameterLoader';
import { childAppsConfig } from './childAppsConfig';
import { IAppState, INITIAL_STATE, localStore as shopStore, localStore } from './store/localStore';
import { environment } from './environments/environment';

const STICKY_OFFSET = -60;

let locationChangedHandler: LocationChangedHandler;

export function init(
  urlParameterLoader: UrlParameterLoader,
  partyId?: string,
  role?: string,
  cryptid?: string,
  aid?: string,
  pid?: string,
  vorid?: string,
  copy?: string,
  amisupd?: string,
  azamVorgangsId?: string,
  ngspDeactivated?: string,
  testDaten?: undefined,
  permasession?: boolean,
  accountManager?: string,
): void {
  initErrorHandler();
  initTracerHandler();
  cleanParametersForDevUrls();
  initLocationChangedHandler();
  initShop(
    urlParameterLoader,
    partyId,
    role,
    cryptid,
    aid,
    pid,
    vorid,
    copy,
    amisupd,
    azamVorgangsId,
    ngspDeactivated,
    testDaten,
    permasession,
    accountManager,
  );
}

export function initGlobalEventDispatcher(): void {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  GlobalPut.getInstance().globalEventDispatcherFunction = () => globalEventDispatcher;

  globalEventDispatcher.addStore(App.SHOP, shopStore);
  globalEventDispatcher.addStore(App.SHOP_TOOLBAR, toolbarStore);
  globalEventDispatcher.addStore(App.SHOP_DASHBOARD, dashboardStore);
  globalEventDispatcher.addStore(App.PH, phStore);
  globalEventDispatcher.addStore(App.JHP, jhpStore);
  globalEventDispatcher.addStore(App.RS, rsStore);
  globalEventDispatcher.addStore(App.RS_VERMIETUNG, rsVermietungStore);
  globalEventDispatcher.addStore(App.THH_HUND, thhHundStore);
  globalEventDispatcher.addStore(App.THH_PFERD, thhPferdStore);
  globalEventDispatcher.addStore(App.TGV, tgvStore);
  globalEventDispatcher.addStore(App.SHOP_CHECKOUT, checkoutStore);

  (window as unknown as Record<string, unknown>)['globalEventDispatcher'] = globalEventDispatcher;
}

function initErrorHandler(): void {
  window.addEventListener('error', handleErrorEvent);
}

export const handleErrorEvent = (errorEvent: ErrorEvent): void => {
  const error = errorEvent.error || errorEvent;
  PS20Logger.error(App.SHOP, error, 'Unhandled Exception');

  if (errorEvent.preventDefault) {
    errorEvent.preventDefault();
  }
  if (errorEvent.stopPropagation) {
    errorEvent.stopPropagation();
  }
};

function initTracerHandler(): void {
  const state: IAppState = localStore.getState();
  Dynatrace.dtraceIdentifyUser(state.saveProtocol.correlationId);

  window.addEventListener('beforeunload', () => {
    Dynatrace.dtraceEndSession();
    return true;
  });
}

// URL-Parameter in Dev-Umgebungen für Angular Live Reload anpassen
export function cleanParametersForDevUrls(): void {
  if (!isDevUrl()) {
    return;
  }
  let urlWithParamters = '?' + location.href.split('?')[1];
  if (urlWithParamters.includes('#')) {
    urlWithParamters = urlWithParamters.split('#')[0];
  }
  history.replaceState({}, document.title, urlWithParamters);
}

export function isProdUrl(loc = location): boolean {
  return loc.hostname === 'privatschutz-prod.allianz.de';
}

function initLocationChangedHandler(): void {
  locationChangedHandler = new LocationChangedHandler(
    window,
    GlobalEventDispatcher.getInstance(),
    childAppsConfig,
  );
}

function initShop(
  urlParameterLoader: UrlParameterLoader,
  partyId: string,
  role: string,
  cryptid: string,
  aid: string,
  pid: string,
  vorid: string,
  copy: string,
  amisupd: string,
  azamVorgangsId: string,
  ngspDeactivated: string,
  testDaten: undefined,
  permasession: boolean,
  accountManager: string,
): void {
  const shopInitAction = new GlobalActions().initializeShop(
    buildDatenhaltungObj(
      urlParameterLoader,
      partyId,
      role,
      cryptid,
      aid,
      pid,
      vorid,
      copy,
      amisupd,
      azamVorgangsId,
      ngspDeactivated,
      testDaten,
      permasession,
      accountManager,
    ),
  );
  GlobalEventDispatcher.getInstance().dispatch(shopInitAction);
  startShopWhenInitWasSuccessful();
}
function buildDatenhaltungObj(
  urlParameterLoader: UrlParameterLoader,
  partyId: string,
  role: string,
  cryptid: string,
  aid: string,
  pid: string,
  vorid: string,
  copy: string,
  amisupd: string,
  azamVorgangsId: string,
  ngspDeactivated: string,
  testDaten: undefined,
  permasession: boolean,
  accountManager: string,
): InitializeShopPayload {
  const urlParams = urlParameterLoader.getParameter();
  return {
    kdv: {
      sessionId: urlParams.aid || aid || INITIAL_STATE.transientState.datenhaltung.kdv.sessionId,
      perId: urlParams.pid || pid || INITIAL_STATE.transientState.datenhaltung.kdv.perId || partyId,
      vorId: urlParams.vorid || vorid || INITIAL_STATE.transientState.datenhaltung.kdv.vorId,
      copyFlag:
        Number(urlParams.copy) ||
        Number(copy) ||
        INITIAL_STATE.transientState.datenhaltung.kdv.copyFlag,
      amisupd:
        urlParams.amisupd || amisupd || INITIAL_STATE.transientState.datenhaltung.kdv.amisupd,
    },
    av: {
      cryptId: urlParams.cryptid || INITIAL_STATE.transientState.datenhaltung.av.cryptId || cryptid,
      angebotsBuendelId: INITIAL_STATE.transientState.datenhaltung.av.cryptId,
    },
    sdw: {
      partyId: partyId || INITIAL_STATE.transientState.datenhaltung.sdw.partyId,
      role: role || INITIAL_STATE.transientState.datenhaltung.sdw.role,
      code: urlParams.code || INITIAL_STATE.transientState.datenhaltung.sdw.code,
      state: urlParams.state || INITIAL_STATE.transientState.datenhaltung.sdw.state,
      accountManager:
        accountManager || INITIAL_STATE.transientState.datenhaltung.sdw.accountManager,
    },
    permasession: urlParams.permasession === 'true' || permasession,
    azamVorgangsId:
      urlParams.azamVorgangsId ||
      azamVorgangsId ||
      INITIAL_STATE.transientState.datenhaltung.azamVorgangsId,
    ngspDeactivated: urlParams.ngspDeactivated === 'true' || ngspDeactivated === 'true',
    testDaten: urlParams.TestDaten || testDaten,
    customAngebotsdatum: !isProdUrl() && urlParams.angebotsdatum ? urlParams.angebotsdatum : null,
  };
}

export const logAppInitializedConditions = (subject: string, timeoutInMs: number): void => {
  PS20Logger.info(
    App.SHOP,
    `${subject} ran into ${timeoutInMs} ms timeout.`,
    `\
        isAuthenticated: ${isAuthenticated()},\
        featureToggleLoaded: ${featureToggleLoaded()},\
        amisDataInitialized: ${amisDataInitialized()},\
        avLoadingFinishedIfNeeded: ${avLoadingFinishedIfNeeded()},\
        vorvertraegeLoaded: ${vorvertraegeLoaded()},\
        appsInitializedInState: ${shopStore.getState().transientState.appIsInitialized};\
      `,
  );
};

export const startShopWhenInitWasSuccessful = (): Promise<void> => {
  /*
   * authentication was not executed at all --> listen to the local store to be informed about (auth) events
   */
  const unsubscribe = localStore.subscribe(() => startShopHandlerWithUnsubscribe(unsubscribe));

  const initializationTimeout = getInitializationTimeOut();

  return new Promise<void>(res => {
    setTimeout(() => {
      handleShopInitializationTimeOut(unsubscribe, initializationTimeout);
      res();
    }, initializationTimeout);
  });
};

export const getInitializationTimeOut = (): number => {
  let timeout = 180000;
  if (environment.production === false) {
    timeout = 500000;
  }
  return timeout;
};

export const startShopHandlerWithUnsubscribe = (unsubscribe: Unsubscribe): void => {
  if (checkIsInitializedCondition()) {
    unsubscribe();
    locationChangedHandler.emitCurrentLocation();
    setupNavigationBarStickyness();
    shopStore.dispatch(appIsInitilized());
    initialNavigation();
  }
};

export const checkIsInitializedCondition = (): boolean =>
  isAuthenticated() &&
  featureToggleLoaded() &&
  amisDataInitialized() &&
  avLoadingFinishedIfNeeded() &&
  vorvertraegeLoaded();

export const handleShopInitializationTimeOut = (
  unsubscribe: Unsubscribe,
  initializationTimeout: number,
): void => {
  if (!checkIsInitializedCondition() && errorPageIsNotOpen()) {
    unsubscribe();
    if (!isAuthenticated()) {
      GlobalEventDispatcher.getInstance().dispatch(
        new GlobalActions().authenticationFailed('Auth took too long'),
      );
    } else {
      logAppInitializedConditions('Shop-Initialization', initializationTimeout);
    }
    localStore.dispatch(new GlobalActions().errorPageRequested());
  }
};

export function errorPageIsNotOpen(): boolean {
  return !localStore.getState().transientState.errorPageOpen;
}

export function initialNavigation(): void {
  if (location.hash === '#/') {
    location.hash = '/dashboard';
  }
}

export function avLoadingFinishedIfNeeded(): boolean {
  if (localStore.getState().transientState.datenhaltung.av.cryptId == null) {
    return true;
  }

  return localStore.getState().transientState.avLoaded === true;
}

export const isAuthenticated = (): boolean => {
  return (
    localStore.getState().transientState.datenhaltung.auth.authenticated &&
    featureToggleLoaded() &&
    Object.values(Role)
      .map(role => role.toString())
      .includes(localStore.getState().transientState.role)
  );
};

function featureToggleLoaded(): boolean {
  return localStore.getState().transientState.featureToggles != null;
}

export function vorvertraegeLoaded(): boolean {
  if (localStore.getState().transientState.role === Role.SPS) {
    return true;
  }

  const loadVorvertraegeStatus = localStore.getState().transientState.loadVorvertraege;

  return (
    (loadVorvertraegeStatus.kundennummerReceived && !loadVorvertraegeStatus.kundennummer) ||
    loadVorvertraegeStatus.loadedOrSkipped
  );
}

export function amisDataInitialized(): boolean {
  if (localStore.getState().transientState.role === Role.SPS) {
    return true;
  }
  return localStore.getState().amisDataReceived;
}

function setupNavigationBarStickyness(): void {
  stickybits('#shop-toolbar-app-root', {
    stickyBitStickyOffset: STICKY_OFFSET,
  });
}
